import React, { useMemo, useState } from 'react';

import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { BasicModal, Dropdown, EmptyState, Tab, TabPanel, Tabs } from '@hz-design-system/web-ui';

import ExternalReviewsContent from './ExternalReviewsContent';

import type { UserReviews } from '../types';

import Classes from './ReviewsDialog.module.scss';

const INTERNAL_REVIEWS_SYSTEM = 'INTERNAL_REVIEWS';

const getLocationKey = (entry: UserReviews) => [entry.sellerName, entry.postCode].filter(Boolean).join(' ');

export const buildDropdownOptions = (entries: UserReviews[], fallback: string): { label: string; value: string }[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const entry of entries) {
    const name = entry.sellerName ?? fallback;
    if (seen.has(name)) duplicates.add(name);
    else seen.add(name);
  }

  return entries.map((entry, i) => {
    const name = entry.sellerName ?? fallback;
    const label = duplicates.has(name) && entry.postCode ? `${name} (${entry.postCode})` : name;
    return { label, value: String(i) };
  });
};

type ReviewsDialogProps = {
  onClose: () => void;
  onTabClick?: (reviewSystem?: string) => void;
  reviews: UserReviews[];
  sellerName: string;
  internalReviewsUrl?: string;
  onExternalReviewsUrlClick?: () => void;
  onLocationFocus?: () => void;
  onLocationChange?: (sellerName: string) => void;
  disableMultiLocation?: boolean;
  initialSellerName?: string;
  initialPostcode?: string;
};

const ReviewsDialog = ({
  onClose,
  onTabClick,
  reviews,
  sellerName,
  internalReviewsUrl,
  onExternalReviewsUrlClick,
  onLocationChange,
  onLocationFocus,
  disableMultiLocation = false,
  initialSellerName,
  initialPostcode,
}: ReviewsDialogProps) => {
  const { t } = useI18nContext();
  const [activeTab, setActiveTab] = useState(0);
  const [locationKeyBySystem, setLocationKeyBySystem] = useState<Record<string, string>>({});

  const reviewGroups = useMemo(() => {
    const seen = new Map<string, UserReviews[]>();
    for (const r of reviews.filter(
      (review) => review.reviewSystem === INTERNAL_REVIEWS_SYSTEM || !!review.numberOfReviews,
    )) {
      const key = r.reviewSystem ?? '';
      if (!seen.has(key)) seen.set(key, []);
      seen.get(key)?.push(r);
    }

    return Array.from(seen.entries()).map(([system, entries]) => ({
      reviewSystem: system,
      entries: disableMultiLocation ? entries.slice(0, 1) : entries,
    }));
  }, [reviews, disableMultiLocation]);

  if (reviewGroups.length === 0) {
    return (
      <BasicModal isOpen onClose={onClose} title={t('reviewsDialogUi:dialog.title')} size="l">
        <div className={Classes.emptyStateWrapper}>
          <EmptyState title={t('reviewsDialogUi:dialog.empty.title')} illustration="empty-state-star" />
        </div>
      </BasicModal>
    );
  }

  const showTabs = reviewGroups.length > 1;

  const handleTabChange = (index: number) => {
    onTabClick?.(reviewGroups[index]?.reviewSystem);
    setActiveTab(index);
  };

  const getActiveIndex = (group: (typeof reviewGroups)[number]): number => {
    const savedKey = locationKeyBySystem[group.reviewSystem];
    if (savedKey !== undefined) {
      const idx = parseInt(savedKey, 10);
      if (!isNaN(idx) && idx >= 0 && idx < group.entries.length) return idx;
    }
    if (initialSellerName || initialPostcode) {
      const initialKey = [initialSellerName, initialPostcode].filter(Boolean).join(' ');
      const idx = group.entries.findIndex((e) => getLocationKey(e) === initialKey);
      if (idx !== -1) return idx;
    }
    return 0;
  };

  return (
    <BasicModal isOpen onClose={onClose} title={t('reviewsDialogUi:dialog.title')} size="l">
      {showTabs && (
        <Tabs activeTab={activeTab} setActiveTab={handleTabChange}>
          {reviewGroups.map((group) => (
            <Tab key={group.reviewSystem} label={t(`reviewsDialogUi:dialog.reviewSystem.${group.reviewSystem}`)} />
          ))}
        </Tabs>
      )}

      {reviewGroups.map((group, index) => {
        const showLocationDropdown = group.entries.length > 1;
        const activeIndex = getActiveIndex(group);
        const activeReview = group.entries[activeIndex];
        const dropdownOptions = buildDropdownOptions(group.entries, sellerName);

        return (
          <TabPanel key={group.reviewSystem} activeTab={activeTab} tab={index}>
            {showLocationDropdown && (
              <div className={Classes.locationDropdown}>
                <Dropdown
                  labelText={t('reviewsDialogUi:dialog.location.label')}
                  options={dropdownOptions}
                  value={String(activeIndex)}
                  onFocus={onLocationFocus}
                  onChange={(e) => {
                    const selectedKey = e.target.value;
                    setLocationKeyBySystem((prev) => ({
                      ...prev,
                      [group.reviewSystem]: selectedKey,
                    }));
                    const selectedEntry = group.entries[parseInt(selectedKey, 10)];
                    onLocationChange?.(selectedEntry?.sellerName ?? sellerName);
                  }}
                />
              </div>
            )}

            {activeReview &&
              (activeReview.reviewSystem === INTERNAL_REVIEWS_SYSTEM ? (
                <iframe className={Classes.internalReviews} src={internalReviewsUrl} />
              ) : (
                <ExternalReviewsContent
                  sellerName={activeReview.sellerName ?? sellerName}
                  reviewsData={activeReview}
                  onExternalReviewsUrlClick={onExternalReviewsUrlClick}
                />
              ))}
          </TabPanel>
        );
      })}
    </BasicModal>
  );
};

export default ReviewsDialog;
